import { Radio, Switch, Modal } from 'antd';
import React, { useMemo } from "react";
import InfoTooltip from "../../../components/InfoTooltip";
import { useAuth } from '../../../providers/AuthProvider';

const CommitmentOptions = {
    1: {
        title: 'Side Hustler',
        short: (
            <span><b>I'm employed, looking for side-hustle</b> freelance roles (part-time) for supplemental
                income</span>),
        description: (
            <span><b>I'm employed and looking for side-hustle</b> freelance assignments (part-time) for supplemental
                income</span>),
        display: (
            <span>Talent is permanently employed but performs freelance projects on the side for additional income.</span>
        )
    },
    3: {
        title: 'Full Time Freelancer',
        short: (
            <span><b>I'm seeking independent freelance/contractor</b> work as my primary source of income</span>),
        description: (
            <span><b>I'm seeking independent freelance/contractor</b> assignments as my primary source of income</span>),
        display: (
            <span>Talent performs freelance and contract work assignments as their primary source of income.</span>
        )

    },
}

const DashboardPreferences = ({ onChange, isDisabled }) => {
    const { auth } = useAuth();
    const preferences = TB.pick(TB.cloneDeep(auth), ['is_available', 'talent_commitment_level', 'has_contract_to_hire_interest', 'has_full_time_employment_interest'])

    const handleChange = (params) => {
        const updatedModel = { ...preferences, ...params };
        return onChange(updatedModel);
    }

    const isApproved = auth.isProfileApproved?.();

    const profileState = useMemo(() => {
        if (auth.isProfileInProgress?.()) return 'in_progress';
        if (auth.isProfileReadyForApproval?.()) return 'ready_for_approval';
        if (auth.isPendingProfileApproved?.()) return 'under_review';
        if (auth.isProfileApproved?.()) return 'approved';
        if (auth.latest_appeal?.is_rejected) return 'appeal_rejected';
        if (auth.latest_appeal) return 'appeal_pending';
        if (auth.isProfileRejected?.()) return 'rejected';
        return null;
    }, [auth]);

    const profileStatusMessage = useMemo(() => {
        switch (profileState) {
            case 'in_progress':
                return 'Complete your profile to submit for approval';
            case 'ready_for_approval':
                return 'Submit your profile for Wripple to review';
            case 'under_review':
                return 'Profile will be visible after Wripple approves your submission';
            case 'rejected':
                return 'Profile was not approved — submit an appeal for review';
            case 'appeal_pending':
                return 'Your appeal is under review by the Wripple team';
            case 'appeal_rejected':
                return 'Appeal was not approved — you may submit a new appeal';
            default:
                return null;
        }
    }, [profileState]);

    return (
        <div className="flex flex-col gap-2 divide-y">
            <div className="flex gap-1 divide-x items-center">
                <p className="text-xs font-semibold min-w-[33%] max-w-[155px]">
                    {isApproved ? 'Profile Visible?' : 'Open to Work?'}
                </p>

                <div className="flex gap-3 items-center flex-grow pl-3">
                    <Switch
                        size="default"
                        checked={preferences.is_available}
                        onChange={async value => {
                            if (!value) {

                                if (preferences.talent_commitment_level === 3 && !TB.isProdEnv()) {
                                    const confirmVisibility = await TB.alert('warning', 'Profile Visibility',
                                        'Setting your profile Side Hustler will allow you to still be found on the platform but will limit job notifications. This will keep your Profile Visibility on with limited alerts.',
                                        [
                                            { title: 'Yes, change to Side Hustler' },
                                            { title: 'No, turn off my profile' }
                                        ]
                                    )

                                    if (confirmVisibility) {
                                        return handleChange({ "talent_commitment_level": 1 })
                                    }

                                    await TB.wait(400)
                                }

                                const confirm = await TB.sysAlert('talent.turn_off_availability_warning');

                                if (!confirm) {
                                    return;
                                }
                            }

                            return handleChange({ is_available: value })
                        }}

                        disabled={isDisabled}
                    />
                    <div>
                        <p className="text-xxs">
                            {isApproved
                                ? <>My profile <strong>{preferences.is_available ? 'WILL' : 'WILL NOT'}</strong> show up on the Wripple platform</>
                                : <>I'm {preferences.is_available ? 'open' : 'not open'} to new projects</>
                            }
                        </p>
                        {!isApproved && profileStatusMessage && (
                            <p className="text-xxs text-amber-600 mt-0.5">
                                <i className="fas fa-info-circle mr-1" />
                                {profileStatusMessage}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex gap-1 divide-x items-start pt-2">
                <p className="text-xs font-semibold min-w-[33%] max-w-[155px] leading-[28px]">Work Status</p>

                <div className="flex flex-row gap-2 items-start flex-grow pl-3 flex-wrap lg:flex-nowrap">
                    <Radio.Group
                        className="flex gap-3"
                        onChange={e => handleChange({ "talent_commitment_level": e.target.value })}
                        disabled={isDisabled}
                        value={Number(preferences.talent_commitment_level)}
                        optionType="button"
                        buttonStyle="solid"
                        name="talent_commitment_level"
                    >
                        {Object.keys(CommitmentOptions).map(key => {
                            return (
                                <span id={`commitment-level-${Number(key)}`} key={CommitmentOptions[key].title}>
                                    <Radio value={Number(key)}

                                        className={TB.mapClass(
                                            "flex flex-col items-start justify-center text-xxs py-1 px-2 h-[28px] leading-normal whitespace-no-wrap",
                                            {
                                                "pointer-events-none": Number(preferences.talent_commitment_level) === Number(key),
                                            })}>
                                        {CommitmentOptions[key].title}
                                    </Radio>
                                </span>
                            )
                        })}
                    </Radio.Group>

                    {CommitmentOptions[preferences.talent_commitment_level] && (
                        <div>
                            <p className="text-xxs">
                                {CommitmentOptions[preferences.talent_commitment_level]?.short}
                            </p>
                            {Object.keys(CommitmentOptions).map(key => {
                                if (Number(key) === Number(preferences.talent_commitment_level)) {
                                    return null;
                                }
                                return (
                                    <InfoTooltip key={`tooltip-${key}`} target={`commitment-level-${Number(key)}`} placement="top" type="dark">
                                        <p className="text-white text-xs italic py-2 px-0 m-0">
                                            {CommitmentOptions[key]?.description}
                                        </p>
                                    </InfoTooltip>
                                )
                            })}
                        </div>
                    )}

                </div>
            </div>


            <div className="flex gap-1 divide-x items-center pt-2">
                <p className="text-xs font-semibold min-w-[33%] max-w-[155px] leading-[28px]">Interested in perm job?</p>

                <div className="flex flex-row gap-2.5 items-center flex-grow pl-3">
                    <Radio.Group
                        className="flex gap-2"
                        onChange={e => handleChange({ "has_full_time_employment_interest": e.target.value })}
                        disabled={isDisabled}
                        value={preferences.has_full_time_employment_interest}
                        optionType="button"
                        buttonStyle="solid"
                        name="has_full_time_employment_interest"
                    >
                        <Radio value={true}
                            className="flex flex-col items-start justify-center text-xxs py-1 px-2 h-[28px] leading-normal whitespace-no-wrap">
                            Yes
                        </Radio>
                        <Radio value={false}
                            className="flex flex-col items-start justify-center text-xxs py-1 px-2 h-[28px] leading-normal whitespace-no-wrap">
                            No
                        </Radio>
                    </Radio.Group>
                    <p className="text-xxs">
                        I <strong>{preferences.has_full_time_employment_interest ? 'AM' : 'AM NOT'}</strong> interested in
                        contract to hire opportunities
                    </p>
                </div>
            </div>
        </div>
    )
}

export default DashboardPreferences;

export {
    CommitmentOptions
}